import { ArrowDown, ArrowUp, ChevronsUpDown } from "lucide-react";
import * as React from "react";

import { cn } from "@/lib/utils";

/**
 * Table primitives. Canonical cinch_ops data-table usage:
 *
 *   <Table containerClassName="max-h-[calc(100vh-16rem)]">
 *     <TableHeader>
 *       <TableRow>
 *         <TableHead sortable sortDirection={sort.key === "customer" ? sort.dir : null}
 *                    onSort={() => toggleSort("customer")}>
 *           Customer
 *         </TableHead>
 *         <TableHead align="right">MRR</TableHead>
 *         <TableHead>Status</TableHead>
 *       </TableRow>
 *     </TableHeader>
 *     <TableBody zebra>
 *       {rows.map((r) => (
 *         <TableRow key={r.id} clickable selected={r.id === selectedId} onClick={() => select(r.id)}>
 *           <TableCell>{r.customer}</TableCell>
 *           <TableCell numeric>{fmtMoney(r.mrr)}</TableCell>
 *           <TableCell><StatusBadge value={r.status} /></TableCell>
 *         </TableRow>
 *       ))}
 *     </TableBody>
 *   </Table>
 *
 * Selected rows pick up `bg-power-light`. Clickable rows get cursor-pointer
 * and a more visible hover. Zebra adds a very faint even-row tint that the
 * selection still wins over.
 *
 * The header is sticky by default (opaque `bg-muted` so body rows don't bleed
 * through it). It only *visibly* sticks when the table scrolls inside a
 * bounded box, so give the table a max-height via `containerClassName` to turn
 * it into a contained scroll panel — e.g.
 * <Table containerClassName="max-h-[calc(100vh-16rem)]">. The cap lives on the
 * caller, not here, because the right value depends on how much chrome sits
 * above the table on that page. Without a cap the whole page scrolls and the
 * header just rides along (harmless). Pass `sticky={false}` to opt out.
 */

export const Table = React.forwardRef<
  HTMLTableElement,
  React.HTMLAttributes<HTMLTableElement> & {
    /** Class for the scroll container (the element that actually scrolls).
     *  Set a max-height here to make the table a contained scroll panel; the
     *  sticky header then pins to the top of that panel. */
    containerClassName?: string;
  }
>(({ className, containerClassName, ...props }, ref) => (
  <div className={cn("relative w-full overflow-auto", containerClassName)}>
    <table
      ref={ref}
      className={cn("w-full caption-bottom text-sm", className)}
      {...props}
    />
  </div>
));
Table.displayName = "Table";

export const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement> & { sticky?: boolean }
>(({ className, sticky = true, ...props }, ref) => (
  <thead
    ref={ref}
    className={cn(
      // Opaque (not bg-muted/40) so scrolled body rows don't show through the
      // sticky header.
      "border-b bg-muted",
      sticky && "sticky top-0 z-10",
      className,
    )}
    {...props}
  />
));
TableHeader.displayName = "TableHeader";

export const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement> & {
    /** Very faint even-row tint. Selection still wins over it. */
    zebra?: boolean;
  }
>(({ className, zebra, ...props }, ref) => (
  <tbody
    ref={ref}
    className={cn(
      "[&_tr:last-child]:border-0",
      // Zebra is excluded on :hover so the row-level hover style isn't
      // shadowed by zebra's higher specificity (nth-child + :not is more
      // specific than :hover alone).
      zebra && "[&_tr:nth-child(even):not([data-state=selected]):not(:hover)]:bg-muted/30",
      className,
    )}
    {...props}
  />
));
TableBody.displayName = "TableBody";

export interface TableRowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  selected?: boolean;
  clickable?: boolean;
}

export const TableRow = React.forwardRef<HTMLTableRowElement, TableRowProps>(
  ({ className, selected, clickable, ...props }, ref) => (
    <tr
      ref={ref}
      data-state={selected ? "selected" : undefined}
      className={cn(
        "border-b transition-colors",
        // Hover + cursor only on clickable rows (semantically: only rows you
        // can act on should signal that). Header rows don't pass `clickable`,
        // so they stay flat.
        clickable && "cursor-pointer",
        clickable &&
          (selected
            ? "bg-power-light hover:bg-power/25"
            : "hover:bg-power/20"),
        // Selected without clickable still picks up the selection color.
        !clickable && selected && "bg-power-light",
        className,
      )}
      {...props}
    />
  ),
);
TableRow.displayName = "TableRow";

export interface TableHeadProps extends React.ThHTMLAttributes<HTMLTableCellElement> {
  align?: "left" | "right" | "center";
  /** Makes the header a sort toggle. Pair with sortDirection + onSort. */
  sortable?: boolean;
  /** "asc" | "desc" | null — null renders the neutral up-down indicator. */
  sortDirection?: "asc" | "desc" | null;
  onSort?: () => void;
}

export const TableHead = React.forwardRef<HTMLTableCellElement, TableHeadProps>(
  ({ className, align = "left", sortable, sortDirection, onSort, children, ...props }, ref) => {
    const base =
      "h-10 bg-muted px-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground";
    const alignment =
      align === "right"
        ? "text-right"
        : align === "center"
        ? "text-center"
        : "text-left";
    if (sortable) {
      const Icon =
        sortDirection === "asc"
          ? ArrowUp
          : sortDirection === "desc"
          ? ArrowDown
          : ChevronsUpDown;
      const active = sortDirection != null;
      return (
        <th ref={ref} className={cn(base, alignment, className)} aria-sort={
          sortDirection === "asc" ? "ascending" : sortDirection === "desc" ? "descending" : "none"
        } {...props}>
          <button
            type="button"
            onClick={onSort}
            className={cn(
              "inline-flex items-center gap-1.5 select-none text-left hover:text-foreground focus-visible:outline-none focus-visible:text-foreground transition-colors",
              align === "right" && "flex-row-reverse",
              active && "text-foreground",
            )}
          >
            <span>{children}</span>
            <Icon className={cn("h-3.5 w-3.5", !active && "opacity-50")} />
          </button>
        </th>
      );
    }
    return (
      <th ref={ref} className={cn(base, alignment, className)} {...props}>
        {children}
      </th>
    );
  },
);
TableHead.displayName = "TableHead";

export interface TableCellProps extends React.TdHTMLAttributes<HTMLTableCellElement> {
  align?: "left" | "right" | "center";
  /** Renders numeric text (tabular-nums, right-aligned by default). */
  numeric?: boolean;
}

export const TableCell = React.forwardRef<HTMLTableCellElement, TableCellProps>(
  ({ className, align, numeric, ...props }, ref) => {
    const effectiveAlign = align ?? (numeric ? "right" : "left");
    return (
      <td
        ref={ref}
        className={cn(
          "px-3 py-2.5 align-middle",
          effectiveAlign === "right" && "text-right",
          effectiveAlign === "center" && "text-center",
          effectiveAlign === "left" && "text-left",
          numeric && "tabular-nums",
          className,
        )}
        {...props}
      />
    );
  },
);
TableCell.displayName = "TableCell";

/**
 * Optional totals row rendered after the body. Sits in a semantic
 * ``<tfoot>`` so screen readers + CSV export tools recognise it as a
 * summary. Styling mirrors the header chrome (slightly heavier text,
 * top border) so a glance tells the eye "this is a roll-up, not just
 * another data row". Pair with cells styled ``font-semibold`` to
 * complete the look.
 */
export const TableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn(
      "border-t-2 border-border bg-muted/60 font-medium [&_tr]:border-0",
      className,
    )}
    {...props}
  />
));
TableFooter.displayName = "TableFooter";

export const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  React.HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <caption ref={ref} className={cn("mt-4 text-sm text-muted-foreground", className)} {...props} />
));
TableCaption.displayName = "TableCaption";

export const TableEmpty = ({
  children,
  colSpan,
}: {
  children: React.ReactNode;
  colSpan: number;
}) => (
  <tr>
    <td colSpan={colSpan} className="py-12 text-center text-sm text-muted-foreground">
      {children}
    </td>
  </tr>
);
